import { Component, OnInit, Inject, LOCALE_ID } from '@angular/core';
import { LoginPBI } from './connection-step-a/connection-step-a.component';
import { PBIService } from '../../services/pbi/pbi.service';
import { UserService } from '../../services/user/user.service';
export interface ConnectionInfo {
  authType: 'user_password' | 'client_credentials';
  user?: string;
  applicationId?: string;
  tenantId?: string;
  hasPassword?: boolean;
  hasClientSecret?: boolean;
}
@Component({
  selector: 'app-connection-wizard',
  templateUrl: './connection-wizard.component.html',
  styleUrls: ['./connection-wizard.component.scss']
})
export class ConnectionWizardComponent implements OnInit {

  public loginData = new LoginPBI();
  public isLoading: boolean;
  public errorStep1: string;
  public errorStep2: string;
  public errorStep3: string;
  public stage: number;
  public loadingText: string;
  public testResult: { success: boolean; message: string } | null = null;
  public applicationName = 'Reports Feed App';
  public applicationId: string;
  public connectionInfo: ConnectionInfo | null = null;
  public isEditMode: boolean = false;
  public editHasPassword: boolean = false;
  public editHasClientSecret: boolean = false;

  constructor(
    private pbiService: PBIService,
    private userService: UserService,
    @Inject(LOCALE_ID) public locale: string,
  ) {}

  isEnglishLocale() {
    return !(typeof this.locale === 'string' && this.locale.toLowerCase().substring(0, 2) === 'pt');
  }

  async ngOnInit() {
    this.isLoading = true;
    this.loadingText = this.isEnglishLocale() ? 'Getting your user data' : 'Carregando seus dados';
    const user = await this.userService.get();
    const hasCredentials = user.user != null || user.authType === 'client_credentials';
    if (!hasCredentials) {
      this.stage = 1;
    } else if (user.authType !== 'client_credentials' && user.email === user.applicationId) {
      this.stage = 2;
    } else {
      this.applicationId = user.applicationId;
      this.loadingText = this.isEnglishLocale() ? 'Checking application permissions' : 'Verificando permissão da aplicação';
      this.loginData.user = user.user;
      await this.verifyConcession();
    }
    this.isLoading = false;
  }

  async onLogin(loginData: LoginPBI) {
    this.isEditMode = false;
    this.isLoading = true;
    this.loadingText = this.isEnglishLocale() ? 'Checking your credentials' : 'Verificando suas credenciais';
    try {
      const result = await this.pbiService.login(loginData);
      this.errorStep1 = null;
      const user = await this.userService.get();
      if (user.email === user.applicationId) {
        this.stage = 2;
        this.onRegister(null);
        return;
      } else {
        this.applicationId = user.applicationId;
        this.loadingText = this.isEnglishLocale() ? 'Checking application permissions' : 'Verificando permissão da aplicação';
        this.loginData.user = user.user;
        await this.verifyConcession();
      }
    } catch(e) {
      console.log(e);
      const err1: any = e;
      this.errorStep1 = (err1.error && err1.error.message) || (this.isEnglishLocale() ? 'Login failed' : 'Falha no login');
    }
    this.isLoading = false;
  }

  async onRegister(event: Event) {
    this.isLoading = true;
    this.loadingText = this.isEnglishLocale() ? 'Registering the application' : 'Registrando a aplicação';
    try {
      const result = await this.pbiService.register();
      this.errorStep2 = null;
      const user = await this.userService.get();
      if (user.email === user.applicationId) {
        if (this.isEnglishLocale()) {
          this.errorStep2 = 'An unexpected error occurred while registering an application. Try again or '
        } else {
          this.errorStep2 = 'Um erro inesperado ocorreu no registro da aplicação. Tente novamente em breve ou entre em contato no chat ao lado';
        }
      } else {
        this.applicationId = user.applicationId;
        this.loadingText = this.isEnglishLocale() ? 'Checking application permissions' : 'Verificando permissão da aplicação';
        this.loginData.user = user.user;
        await this.verifyConcession();
      }
    } catch(e) {
      console.log(e);
      const err2: any = e;
      this.errorStep2 = (err2.error && err2.error.message) || (this.isEnglishLocale() ? 'Registration failed' : 'Falha no registro');
    }
    this.isLoading = false;
  }

  async onVerify() {
    this.isLoading = true;
    await this.verifyConcession();
    this.isLoading = false;
  }

  async onTestConnection() {
    this.isLoading = true;
    this.testResult = null;
    this.loadingText = this.isEnglishLocale() ? 'Testing connection' : 'Testando conexão';
    try {
      await this.pbiService.checkConnection();
      this.testResult = {
        success: true,
        message: this.isEnglishLocale() ? 'Connection successful' : 'Conexão bem-sucedida'
      };
    } catch(e) {
      const errTest: any = e;
      this.testResult = {
        success: false,
        message: (errTest.error && errTest.error.message) || (this.isEnglishLocale() ? 'Connection failed' : 'Falha na conexão')
      };
    }
    this.isLoading = false;
  }

  onEditConnection() {
    this.testResult = null;
    this.loginData = new LoginPBI();
    const info = this.connectionInfo!;
    this.loginData.authType = info.authType || 'user_password';
    // Pre-fill all non-secret fields for both tabs (applicationId and clientId share the same Azure app ID)
    this.loginData.applicationId = info.applicationId || '';
    this.loginData.clientId = info.applicationId || '';
    this.loginData.user = info.user || '';
    this.loginData.tenantId = info.tenantId || '';
    // password and clientSecret intentionally left empty — backend keeps existing when not provided
    this.editHasPassword = !!info.hasPassword;
    this.editHasClientSecret = !!info.hasClientSecret;
    this.isEditMode = true;
    this.errorStep1 = '';
    this.stage = 1;
  }

  async verifyConcession() {
    try {
       const data = await this.pbiService.checkConnection();
       const user = await this.userService.get();
       this.connectionInfo = {
         authType: user.authType || 'user_password',
         user: user.user,
         applicationId: user.applicationId,
         tenantId: user.tenantId,
         hasPassword: user.hasPassword,
         hasClientSecret: user.hasClientSecret,
       };
       this.stage = 4;
    } catch(e) {
      const err: any = e;
      const msg: string = (err.error && err.error.message) || '';
      if (msg.indexOf('Error validating credentials') > 0) {
        this.stage = 1;
        this.errorStep1 = this.isEnglishLocale() ? 'Invalid credentials. Please check your details and try again.' : 'Credenciais inválidas. Verifique seus dados e tente novamente.';
        return;
      }
      this.stage = 3;
      this.errorStep3 = this.isEnglishLocale() ? 'Application permissions has not been granted' : 'A permissão no Azure não foi concedida ao aplicativo';
    }
  }
}
